import Typography from '@mui/material/Typography';
import { Drawer } from '@openx/components-core/lib/Drawer/Drawer.tsx';
import { type DrawerAction, DrawerActionBar } from '@openx/components-core/lib/Drawer/DrawerActionBar.tsx';
import { DrawerHeader } from '@openx/components-core/lib/Drawer/DrawerHeader.tsx';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { SeatTransferListReadOnly } from '@openx/components-core/lib/SearchTransferList/variants/SeatTransferList/SeatTransferListReadOnly.tsx';
import { toPercentString } from '@openx/utils/lib/toPercentString.ts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useAuth } from '../../../components/AuthContext/context.ts';
import { isMasterOrganisation } from '../../../permissions/validators.ts';
import { curatedDealBuyerDiscountSeatTransferListLocalStorageKey } from '../Form/constants.ts';
import { useSeatTransferListFields } from '../Form/sections/hooks/useSeatTransferListFields.tsx';
import { useCuratedDealBuyerDiscountDelete } from '../hooks/useCuratedDealBuyerDiscountDelete.tsx';
import { useCuratedDealBuyerDiscountFetch } from '../hooks/useCuratedDealBuyerDiscountFetch.ts';

export type CuratedDealBuyerDiscountsDetailsParams = {
  curatedDealBuyerDiscountUid: string;
};

export const CuratedDealBuyerDiscountDetailsDrawer = () => {
  const { session } = useAuth();
  const { deleteCuratedDealBuyerDiscount } = useCuratedDealBuyerDiscountDelete();
  const navigate = useNavigate();
  const { curatedDealBuyerDiscountUid = '' } = useParams<CuratedDealBuyerDiscountsDetailsParams>();
  const { curatedDealBuyerDiscount, demandPartnerName, isError, isLoading, seatList } =
    useCuratedDealBuyerDiscountFetch(curatedDealBuyerDiscountUid);

  const { t } = useTranslation();

  const isDemandPartnerDiscount = typeof curatedDealBuyerDiscount?.demand_partner_id === 'string';
  const isMasterOrg = isMasterOrganisation(session);
  const isOpen = !!curatedDealBuyerDiscountUid;

  const onClose = useCallback(() => {
    navigate('/discounts');
  }, [navigate]);

  const actions = useMemo<DrawerAction[]>(
    () => [
      {
        color: 'error',
        label: t('Delete'),
        onClick: () => curatedDealBuyerDiscount && deleteCuratedDealBuyerDiscount(curatedDealBuyerDiscount, onClose),
      },
      {
        label: t('Edit'),
        href: `/discounts/${curatedDealBuyerDiscount?.uid}/edit`,
      },
    ],
    [curatedDealBuyerDiscount, deleteCuratedDealBuyerDiscount, onClose, t]
  );

  return (
    <Drawer isError={isError} isLoading={isLoading} onClose={onClose} open={isOpen}>
      <DrawerHeader onClose={onClose}>{curatedDealBuyerDiscount?.name}</DrawerHeader>
      <Typography
        variant="h3"
        sx={{
          mb: 1.5,
        }}
      >
        {t('Discount Details')}
      </Typography>

      <FieldDetails title={t('Discount ID')} value={curatedDealBuyerDiscount?.id} />

      <FieldDetails
        title={t('Banner and Video Discount')}
        value={toPercentString(curatedDealBuyerDiscount?.banner_and_video_discount)}
      />

      <FieldDetails title={t('CTV Discount')} value={toPercentString(curatedDealBuyerDiscount?.ctv_discount)} />
      <Typography
        variant="h3"
        sx={{
          mb: 1.5,
          mt: 2,
        }}
      >
        {t('Applied to')}
      </Typography>

      {isDemandPartnerDiscount && <FieldDetails title={t('DSP Partner')} value={demandPartnerName} />}

      {!isDemandPartnerDiscount && (
        <SeatTransferListReadOnly
          items={seatList}
          localStorageKey={curatedDealBuyerDiscountSeatTransferListLocalStorageKey}
          useFieldsConfig={useSeatTransferListFields}
        />
      )}

      {!isMasterOrg && <DrawerActionBar actions={actions} stickyBottom />}
    </Drawer>
  );
};
